/**
 * Convert a timezone-naive `<input type="datetime-local">` value
 * (e.g. "2026-06-18T14:00") into an absolute ISO-8601 string with offset.
 *
 * BUG-1 / APT-TZ-01 (audit 2026-06-16): the booking flow sent the naive string
 * as-is, and the backend (Time.zone = UTC) stored "14:00" as 14:00 UTC, so a BRT
 * user who picked 14:00 saw 11:00. Drag-reschedule already sent an absolute
 * timestamp via Date#toISOString(); this makes creation consistent. `new Date()`
 * interprets the naive value as the browser's local time (same context the agenda
 * renders it back in), so the displayed hour now round-trips to the picked hour.
 *
 * Returns the input unchanged when it is empty or already carries a timezone
 * marker (offset or trailing Z) so callers can pass it through idempotently.
 */
export function localDateTimeToISO(value) {
  if (!value || typeof value !== 'string') return value;
  // Already absolute (has Z or a ±HH:MM offset after the time) — leave as is.
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString();
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Widen a `<input type="date">` value into the datetime the appointment API
 * documents for a reporting window.
 *
 * The `from`/`to` params are `format: date-time` and the backend parses them
 * with a strict ISO-8601 pattern that REQUIRES the time part — a bare
 * "2026-07-17" is rejected with 422, which is what silently emptied the
 * Agendamento report (and its CSV export): the picker produces dates, the API
 * only ever accepted datetimes.
 *
 * The result stays deliberately NAIVE (no Z, no offset) because the backend
 * reads an offsetless value in the ACCOUNT's business timezone. Sending an
 * absolute instant derived from the operator's browser would shift the window
 * whenever the two zones disagree — the same class of bug as APT-TZ-01.
 *
 * `to` takes the end of its day: with 00:00:00 the API's `scheduled_at <= to`
 * would drop everything booked on the last day the user picked.
 *
 * Values that are empty or already carry a time are passed through, so callers
 * can apply these idempotently.
 */
function withDayBoundary(value, boundary) {
  if (!value || typeof value !== 'string') return value;
  if (!DATE_ONLY.test(value)) return value;
  return `${value}T${boundary}`;
}

export function businessDayStart(value) {
  return withDayBoundary(value, '00:00:00');
}

export function businessDayEnd(value) {
  return withDayBoundary(value, '23:59:59');
}

export default { localDateTimeToISO, businessDayStart, businessDayEnd };
